import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { SettingsIcon } from "lucide-react";
import { useMessagesStore } from "stores/messages";

import { formatDateRelative } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { css, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { H1 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import { Pagination } from "simplydo/react/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { EmptyBox } from "components/lib/UI";

const BannerContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isComputer ? "row" : "column")};
  justify-content: ${({ theme }) =>
    theme.sizes.isComputer ? "space-between" : "centre"};
  align-items: ${({ theme }) =>
    theme.sizes.isComputer ? "flex-end" : "centre"};
  h1 {
    margin: 0 0 10px 0;
    color: #3b3b3b;
  }
  .ui.input {
    width: 360px;
  }
`;

const NavigationArea = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  span {
    display: block;
    font-weight: 700;
  }
`;

const messageRowClass = css`
  cursor: pointer;
  &:hover {
    background-color: #e9ebee;
  }
`;

const MessageCentre = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();
  const theme = useTheme();
  const onMessageThreadSelected = useMessagesStore(
    (state) => state.selectThread,
  );

  const [queryState, setQueryState] = useState({ page: 1, query: "" });
  const [messageState, setMessageState] = useState<
    OpenAPI.GET<`/messages/threads`>["response"]
  >({
    messageThreads: [],
    nextPageAvailable: false,
    previousPageAvailable: false,
    total: 0,
    newMessageCount: 0,
    page: 1,
  });
  const [messagesLoading, setMessagesLoading] = useState(false);
  const { page, query } = queryState;

  const fetchThreads = useThrottle(
    (nextPage, nextQuery) => {
      api.messages.getThreads(
        { page: nextPage, query: nextQuery },
        (data) => {
          setMessageState(data);
          setMessagesLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setMessagesLoading(false);
        },
      );
    },
    400,
    [],
  );

  const getThreads = useCallback(
    (nextPage: number, nextQuery: string) => {
      setMessagesLoading(true);
      setQueryState({ page: nextPage, query: nextQuery });
      fetchThreads(nextPage, nextQuery);
    },
    [fetchThreads],
  );

  useEffect(() => {
    getThreads(1, "");
  }, [getThreads]);

  const { messageThreads, total } = messageState;

  return (
    <>
      <div style={{ marginTop: "2rem" }}>
        <Container>
          <BannerContainer>
            <div>
              <H1>{t("messages.title")}</H1>
              <Input
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                loading={messagesLoading}
                placeholder={t("messages.searchPlaceholder")}
                onChange={(e) => getThreads(1, e.target.value)}
                value={query}
              />
            </div>
            <ButtonLink to="/preferences/notifications">
              <Icon icon={SettingsIcon} />
              {t("messages.updateSettings")}
            </ButtonLink>
          </BannerContainer>
        </Container>
      </div>
      <Container style={{ marginTop: 20 }}>
        {messageThreads && messageThreads.length > 0 ? (
          <Table unstackable>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Chat</TableHeaderCell>
                <TableHeaderCell>With</TableHeaderCell>
                {!theme.sizes.isMobile && (
                  <TableHeaderCell>
                    {t("messages.table.lastMessage")}
                  </TableHeaderCell>
                )}
                <TableHeaderCell>
                  {t("messages.table.received")}
                </TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {messageThreads.map((thread) => {
                const { lastMessageAt, lastMessageSnippet = "" } = thread;

                const otherUsers =
                  thread.participantUsers?.filter((u) => u._id !== user?._id) ??
                  [];

                return (
                  <TableRow
                    className={messageRowClass}
                    key={thread._id}
                    onClick={() =>
                      onMessageThreadSelected(thread, !theme.sizes.isComputer)
                    }
                  >
                    <TableCell
                      collapsing
                      style={{
                        maxWidth: 400,
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {thread.seenBy &&
                        user?._id &&
                        thread.seenBy.indexOf(user._id) === -1 && (
                          <Label
                            style={{
                              marginRight: 7,
                              display: theme.sizes.isMobile
                                ? "block"
                                : undefined,
                              maxWidth: theme.sizes.isMobile
                                ? "fit-content"
                                : undefined,
                            }}
                            size="small"
                            color="teal"
                          >
                            New
                          </Label>
                        )}
                      {util.getChatName(user, thread)}
                    </TableCell>
                    <TableCell collapsing>
                      {otherUsers.slice(0, 3).map((p) => (
                        <UserChip
                          key={p._id}
                          user={p}
                          compact="very"
                          style={{ marginRight: 5 }}
                        />
                      ))}
                      {otherUsers.length > 3 && (
                        <Label size="small">
                          +{otherUsers.length - 3} others
                        </Label>
                      )}
                    </TableCell>
                    {!theme.sizes.isMobile && (
                      <TableCell>
                        <div
                          style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            WebkitLineClamp: 2,
                          }}
                        >
                          &quot;{lastMessageSnippet}&quot;
                        </div>
                      </TableCell>
                    )}

                    <TableCell collapsing>
                      {lastMessageAt &&
                        formatDateRelative(lastMessageAt, { addSuffix: true })}
                    </TableCell>
                    <TableCell collapsing>
                      <Button
                        size="small"
                        onClick={() =>
                          onMessageThreadSelected(thread, theme.sizes.isMobile)
                        }
                        color="secondary"
                        variant="outline"
                      >
                        {t("messages.table.view")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : messagesLoading ? (
          <Loader />
        ) : (
          <EmptyBox title={t("messages.empty")} />
        )}
        <NavigationArea>
          <span>{util.pluralise(total, "thread", "total threads")}</span>
          <Pagination
            activePage={page}
            totalPages={Math.ceil(total / 10)}
            onPageChange={(_, { activePage }) =>
              getThreads(activePage as number, query)
            }
          />
        </NavigationArea>
      </Container>
    </>
  );
};

export default MessageCentre;
